import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleStartSaving = () => {
    // Navigate to signup page
    navigate('/signup');
  };

  const faqItems = [
    {
      question: "How does Auric work?",
      answer: "Choose your metal (gold, silver, or platinum), pick your subscription amount and frequency, and we'll automatically charge your card on schedule. Your payments accumulate in your account until you reach the threshold for your target weight. Once you hit that target, we ship the physical metal directly to your door."
    },
    {
      question: "What metals can I collect?",
      answer: "We offer gold, silver, and platinum. Each metal has different weight options — gold is available from 1 gram to 1 troy ounce, silver from 1 to 10 troy ounces, and platinum from 1 gram to 1 troy ounce. Pick what fits your goals."
    },
    {
      question: "When do I receive my metals?",
      answer: "When your accumulated balance reaches the cost of your target weight (including shipping), we purchase the physical metal and ship it to you. The timeline depends on your subscription amount and target — someone putting in $100/month toward 1 gram of gold will hit their target faster than someone putting in $10/month toward 1 ounce."
    },
    {
      question: "What are the subscription options?",
      answer: "You can subscribe weekly, bi-weekly, monthly, or quarterly. Amounts range from $10 to $250 per period. Mix and match however you want — you can even have multiple subscriptions for different metals running at the same time."
    },
    {
      question: "Can I cancel or pause anytime?",
      answer: "Yes. Pause or cancel your subscription from your dashboard with one click. Your accumulated balance stays in your account. You can resume anytime or let it sit until you're ready to continue."
    },
    {
      question: "How is shipping handled?",
      answer: "Shipping costs are deducted from your accumulated balance when we purchase your metal. We ship insured via USPS or UPS to the address on file. You'll get tracking info and delivery notifications along the way."
    }
  ];

  return (
    <div className="landing-page">
      {/* Top Banner */}
      <div className="landing-top-banner">
        <p className="m-0">Start collecting gold, silver, and platinum for only $10/month!</p>
      </div>

      {/* Hero Banner */}
      <section className="landing-hero-banner">
        <div className="max-w-7xl mx-auto">
          <div className="landing-hero-content">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 text-center">
                <h1 className="mb-4">
                  <strong>Start collecting gold, silver, and platinum for just $10/month</strong>
                </h1>
                <p className="mb-4">
                  Deposit $10 or more on your schedule until you've accumulated enough to purchase your precious metal. Once you hit your target, we ship it straight to your door.
                </p>

                <div className="landing-metal-form">
                  <button type="button" className="landing-btn-primary" onClick={handleStartSaving}>
                    Start acquiring precious metals today!
                  </button>
                </div>
              </div>
              <div className="flex-1 landing-header-img-container">
                <img src="/handgold.jpg" alt="Gold in hands" className="w-full h-full object-cover landing-header-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="landing-process-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="landing-lead">A NEW WAY TO PURCHASE PRECIOUS METALS</h2>
            <p className="text-gray-600 mt-1">Deposit $25 every other week and receive 1 gram of gold in about 3 months!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Step 1 */}
            <div>
              <div className="landing-process-card">
                <img src="/images/first.png" alt="Set up deposits" />
                <p>Set up weekly, bi-weekly, or monthly deposits to your account.</p>
              </div>
            </div>

            {/* Step 2 */}
            <div>
              <div className="landing-process-card">
                <img src="/images/second.png" alt="Choose metals" />
                <p>Choose which metals you would like to purchase and add them to your collection.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div>
              <div className="landing-process-card">
                <img src="/images/trd.png" alt="Receive shipment" />
                <p>Once your balance reaches the threshold, your metal will be sent immediately.</p>
              </div>
            </div>

            {/* Step 4 */}
            <div>
              <div className="landing-process-card">
                <img src="/images/4thh.png" alt="Purchase anytime" />
                <p>Purchase metals at any time by adding to your balance on the fly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Precious Metals Section */}
      <section className="landing-precious-metals-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-8 landing-precious-metals-padding">
            {/* Image Column */}
            <div className="flex-1">
              <div className="landing-image-container">
                <img
                  src="/images/phone-girl.jpg"
                  alt="Professional woman with phone"
                  className="landing-section-image"
                />
              </div>
            </div>

            {/* Content Column */}
            <div className="flex-1">
              <div className="lg:pl-8">
                <div className="section-header">WHY COLLECT PRECIOUS METALS?</div>

                <h2 className="main-heading">
                  Collecting precious metals can be a great strategic financial decision
                </h2>

                <div className="benefits-list">
                  <div className="landing-benefit-item">
                    <h3 className="landing-benefit-number">1. Wealth Preservation</h3>
                    <p className="landing-benefit-description">
                      Precious metals have historically been a reliable store of value. They can act as a hedge against
                      economic instability, inflation, and currency devaluation. Holding precious metals can help preserve
                      your wealth over time, as their value tends to increase over the long term.
                    </p>
                  </div>

                  <div className="landing-benefit-item">
                    <h3 className="landing-benefit-number">2. Diversification</h3>
                    <p className="landing-benefit-description">
                      Diversifying your investment portfolio is a key strategy for risk management. Precious metals offer
                      diversification benefits because their value often moves independently of traditional financial assets
                      like stocks and bonds. This can reduce the overall risk in your investment portfolio.
                    </p>
                  </div>

                  <div className="landing-benefit-item">
                    <h3 className="landing-benefit-number">3. Tangible Assets</h3>
                    <p className="landing-benefit-description">
                      Precious metals are tangible assets that you physically own. Unlike digital or paper assets, you can
                      hold and store precious metals, which can provide a sense of security and control over your investments.
                      Additionally, they can be passed down as a form of wealth to future generations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial and FAQ Section */}
      <section className="landing-testimonial-faq-section">
        <div className="max-w-7xl mx-auto px-4">
          {/* FAQ Section */}
          <div className="landing-faq-section">
            <h2 className="landing-faq-title">FAQ</h2>

            <div className="landing-faq-grid">
              {faqItems.map((faq, index) => (
                <div key={index} className="landing-faq-item">
                  <h3 className="landing-faq-question">
                    {faq.question}
                  </h3>
                  <p className="landing-faq-answer">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Designed by Collectors Section */}
      <section className="landing-collectors-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="landing-section-header">WHY WE BUILT AURIC</div>

          {/* Testimonial Quote */}
          <div className="landing-testimonial-quote">
            <p className="landing-quote-text">
              "I believe that collecting precious metals is like acquiring a timeless treasure chest, one that not only
              holds intrinsic value but also provides a sense of security and stability in an ever-changing financial landscape."
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-custom-footer">
        <div className="max-w-7xl mx-auto landing-footer-padding">
          {/* Footer Bottom */}
          <div className="landing-footer-bottom">
            <div className="landing-copyright-text">© {new Date().getFullYear()} <a href="https://horizon-group.io" target="_blank" rel="noopener noreferrer">Horizon Group</a>. All rights reserved. Built with ❤️ by <a href="https://jason.camp" target="_blank" rel="noopener noreferrer">Jason Camp</a></div>
            {/* Privacy / Terms links removed: no such routes exist and there is
                no catch-all, so both rendered a blank page. Restore them once
                real policy pages are published. */}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
